/**
 * Convert a parsed boundary contour into a TAK CoT polygon shape, reusing the
 * shared {@link buildPolygonCotXml} builder. Air and sea boundaries get distinct
 * ARGB stroke/fill colors so operators can toggle and tell them apart.
 */

import type { BoundaryContour } from '../types/boundary.js';
import { buildPolygonCotXml } from './cot-shape-builder.js';

/** TAK ARGB (signed 32-bit) style per category: gold for air, sky-blue for sea. */
const CATEGORY_STYLE = {
  air: { stroke: 0xffffd700 | 0, fill: 0x33ffd700 | 0 },
  sea: { stroke: 0xff00bfff | 0, fill: 0x3300bfff | 0 },
} as const;

/** Polygon stroke weight for published boundaries */
const BOUNDARY_STROKE_WEIGHT = 3;

/** Characters allowed in a generated CoT uid slug */
const SLUG_ALLOWED = new Set('abcdefghijklmnopqrstuvwxyz0123456789-');

/**
 * Build CoT polygon XML for a boundary contour.
 * @param contour   The parsed boundary.
 * @param uidPrefix A caller-unique prefix (e.g. `boundary-3`) ensuring distinct uids.
 */
export function boundaryToCotXml(contour: BoundaryContour, uidPrefix: string): string {
  const style = CATEGORY_STYLE[contour.category];
  return buildPolygonCotXml({
    uid: `${uidPrefix}-${slug(contour.name)}`,
    callsign: contour.name,
    strokeColor: style.stroke,
    strokeWeight: BOUNDARY_STROKE_WEIGHT,
    fillColor: style.fill,
    vertices: contour.points,
  });
}

/** Reduce a name to a uid-safe slug (no regex). */
function slug(name: string): string {
  let out = '';
  for (const char of name.toLowerCase()) {
    out += SLUG_ALLOWED.has(char) ? char : '-';
  }
  return out.length > 0 ? out : 'contour';
}
